if (!customElements.get("quick-add-modal")) {
  customElements.define(
    "quick-add-modal",
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        window.addEventListener("keyup", (evt) => {
          if (
            evt.code === "Escape" &&
            (this.classList.contains("active") ||
              this.classList.contains("quick-add-modal--closing"))
          ) {
            this.hide();
          }
        });

        window.addEventListener("resize", () => {
          this.modalContent
            .querySelectorAll(".js-media-list")
            .forEach((element) => {
              element.swiper?.destroy();
            });
          this.modalContent
            .querySelectorAll(".js-media-sublist")
            .forEach((element) => {
              element.swiper?.destroy();
            });

          setTimeout(() => {
            subSliderInit(this.modalContent, true);
            sliderInit(this.modalContent, true);
          }, 200);
        });
      }

      hide(preventFocus = false) {
        if (preventFocus) this.openedBy = null;

        if (this.classList.contains("quick-add-modal--closing")) {
          this._clearQuickAddCloseAnimation();
          this._quickAddFinalizeRan = false;
          this._finalizeQuickAddTeardown();
          return;
        }

        if (!this.classList.contains("active")) return;

        const content = this.querySelector(".quick-add-modal__content");
        if (!content || !this.modalContent) {
          this.classList.remove("active");
          super.hide();
          return;
        }

        this._quickAddFinalizeRan = false;
        this.dataset.closeAnimating = "true";

        this._quickAddPanelForClose = content;
        this._boundQuickAddPanelTransitionEnd = (event) => {
          if (event.target !== content || event.propertyName !== "transform") {
            return;
          }
          this._finalizeQuickAddTeardown();
        };

        content.addEventListener(
          "transitionend",
          this._boundQuickAddPanelTransitionEnd
        );

        this.classList.add("quick-add-modal--closing");
        this.classList.remove("active");

        this._quickAddCloseFallback = window.setTimeout(() => {
          this._finalizeQuickAddTeardown();
        }, 450);
      }

      _clearQuickAddCloseAnimation() {
        if (this._quickAddCloseFallback) {
          clearTimeout(this._quickAddCloseFallback);
          this._quickAddCloseFallback = null;
        }
        if (
          this._boundQuickAddPanelTransitionEnd &&
          this._quickAddPanelForClose
        ) {
          this._quickAddPanelForClose.removeEventListener(
            "transitionend",
            this._boundQuickAddPanelTransitionEnd
          );
        }
        this._boundQuickAddPanelTransitionEnd = null;
        this._quickAddPanelForClose = null;
      }

      _finalizeQuickAddTeardown() {
        if (this._quickAddFinalizeRan) return;
        this._quickAddFinalizeRan = true;

        this._clearQuickAddCloseAnimation();

        this.classList.remove("quick-add-modal--closing");
        this.dataset.closeAnimating = "false";

        if (!this.modalContent) {
          super.hide();
          return;
        }

        this.modalContent.innerHTML = "";

        this.modalContent.querySelectorAll(".js-media-list").forEach((el) => {
          el.swiper?.destroy();
        });
        this.modalContent
          .querySelectorAll(".js-media-sublist")
          .forEach((el) => {
            el.swiper?.destroy();
          });

        subSliderInit(this.modalContent, true);
        sliderInit(this.modalContent, true);

        super.hide();

        if (this.openedBy?.closest(".collection-product-card")) {
          this.openedBy.focus({ preventScroll: true });
        } else {
          const cardLinks = this.openedBy?.closest(".card__links");
          cardLinks?.nextElementSibling?.focus?.();
        }
      }

      show(opener) {
        if (
          this.classList.contains("quick-add-modal--closing") ||
          this.dataset.closeAnimating === "true"
        ) {
          this._clearQuickAddCloseAnimation();
          this._quickAddFinalizeRan = false;
          this._finalizeQuickAddTeardown();
        }

        opener.setAttribute("aria-disabled", true);
        opener.classList.add("loading");
        const spinner = opener.querySelector(".loading-overlay__spinner");
        if (spinner) {
          spinner.classList.remove("hidden");
        }

        fetch(opener.getAttribute("data-product-url"))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(
              this.modalContent,
              this.productElement.innerHTML,
              opener
            );

            this.injectMediaStylesheets(responseHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.updateImageSizes();
            this.preventVariantURLSwitching();
            setTimeout(() => {
              this.classList.add("active");
            });
            super.show(opener);

            this.addEventListener(
              "transitionend",
              () => {
                const containerToTrapFocusOn = this;
                const focusElement = this.querySelector(
                  ".quick-add-modal__content"
                );
                trapFocus(containerToTrapFocusOn, focusElement);
              },
              { once: true }
            );
          })
          .finally(() => {
            opener.removeAttribute("aria-disabled");
            opener.classList.remove("loading");

            if (opener.querySelector(".loading-overlay__spinner")) {
              opener
                .querySelector(".loading-overlay__spinner")
                .classList.add("hidden");
            }

            this.modalContent
              .querySelectorAll(".js-media-list")
              .forEach((element) => {
                element.swiper?.destroy();
              });

            this.modalContent
              .querySelectorAll(".js-media-sublist")
              .forEach((element) => {
                element.swiper?.destroy();
              });

            subSliderInit(this.modalContent, true);
            sliderInit(this.modalContent, true);

            selectDropDown();
            this.initDropdown();
            this.initProductAccordion();
            this.setScrollClass(this.modalContent);
            this.clearMediaClasses();
          });
      }

      setInnerHTML(element, html, opener) {
        element.innerHTML = html;
        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll("script").forEach((oldScriptTag) => {
          const newScriptTag = document.createElement("script");
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });

        const wrapper = document.createElement("div");
        wrapper.className = "product__full-details-wrapper";

        // Read more button
        const moreBtn = document.createElement("a");
        moreBtn.innerHTML = `<span>${theme.quickviewMore}</span>`;
        moreBtn.setAttribute("href", opener.getAttribute("data-product-url"));
        moreBtn.setAttribute(
          "class",
          "product__full-details button button-link"
        );

        const svgArrow = `
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 2L7.84496 6L4 10" stroke="currentColor" stroke-width="2" stroke-linecap="square"/>
          </svg>
        `;

        wrapper.appendChild(moreBtn);
        wrapper.insertAdjacentHTML("beforeend", svgArrow);

        if (
          element.querySelectorAll(".product__info-column") &&
          element.querySelectorAll(".product__info-column").length > 0
        ) {
          element
            .querySelector(".product__info-container")
            .appendChild(wrapper);
        } else {
          element
            .querySelector(".product__info-container")
            .appendChild(wrapper);
        }

        const copyLink = this.querySelector(".copy-btn");
        copyLink.addEventListener("click", (e) => this.copyURI(e, copyLink));
      }

      calcContainerHeight(element) {
        const topHeigh = this.querySelector(
          ".quick-add-modal__top"
        ).clientHeight;
        const buyBlockHeight = element.querySelector(
          ".product__buy-buttons"
        )?.clientHeight;
        element.style.height = `100%`; // `calc(100% - ${topHeigh + buyBlockHeight + 4}px)`;
      }

      setBuyButtonsHeightVar(element) {
        const modalContent = this.querySelector(".quick-add-modal__content");
        if (!modalContent || !element) return;

        const buyButtons = element.querySelector(".product__buy-buttons");

        const buyButtonsHeight = buyButtons
          ? Math.ceil(buyButtons.getBoundingClientRect().height)
          : null;

        if (buyButtonsHeight == null) return;

        modalContent.style.setProperty(
          "--quick-add-buy-buttons-height",
          `${buyButtonsHeight}px`
        );
      }

      copyURI(e, copyLink) {
        e.preventDefault();

        const url = e?.target?.parentElement?.dataset?.copyHref;

        const handleCopy = (btn, success) => {
          if (success) {
            btn.classList.add("copied");
            setTimeout(() => {
              btn.classList.remove("copied");
            }, 1000);
          } else {
            btn.classList.add("notCopied");
            setTimeout(() => {
              btn.classList.remove("notCopied");
            }, 1000);
          }
        };

        navigator.clipboard.writeText(url).then(
          () => {
            handleCopy(copyLink, true);
          },
          () => {
            handleCopy(copyLink, false);
          }
        );
      }

      toggleScrollClass(element) {
        const infoContainer = element?.querySelector(
          ".product__info-container"
        );

        const baseHeight = element.getBoundingClientRect().height;
        const productMedia = element.querySelector(".product__main");
        if (!productMedia || !infoContainer || !baseHeight) return;
        const productMediaType = productMedia.dataset.imageType;
        const heightRatio =
          productMediaType === "portrait"
            ? 1.266
            : productMediaType === "landscape"
            ? 0.75
            : 1;
        const imageHeight = productMedia.clientWidth * heightRatio;
        const toShowHeight = baseHeight - 16 - imageHeight;

        if (infoContainer.scrollHeight > toShowHeight) {
          element.classList.add("has-scroll");
        } else {
          element.classList.remove("has-scroll");
        }
      }

      setScrollClass(element) {
        const resizeObserver = new ResizeObserver((entries) => {
          const [entry] = entries;

          const dialog = entry.target.querySelector(
            ".quick-add-modal__content-info"
          );
          if (dialog) {
            this.setBuyButtonsHeightVar(element);
            this.calcContainerHeight(element);
            this.toggleScrollClass(dialog);
          }
        });

        if (element) {
          resizeObserver.observe(this);
        }
      }

      initDropdown = () => {
        const getDropdowns = () =>
          this.querySelectorAll(".quick-add-modal__content .dropdown-opener");

        const dropdowns = getDropdowns();
        if (!dropdowns.length) return;

        const closeAllDropdownsExcept = (exception = null) => {
          getDropdowns().forEach((dropdown) => {
            if (dropdown !== exception) {
              dropdown.classList.remove("active");
            }
          });
        };

        dropdowns.forEach((dropdown) => {
          if (dropdown.dataset.dropdownInitialized === "true") return;
          dropdown.dataset.dropdownInitialized = "true";

          dropdown.addEventListener("click", (e) => {
            e.stopPropagation();
            closeAllDropdownsExcept(dropdown);
            dropdown.classList.toggle("active");

            //const next = dropdown.nextElementSibling;
            //trapFocus(next);
          });

          dropdown.addEventListener("keydown", (e) => {
            const next = dropdown.nextElementSibling;
            const el = next.querySelector("li a");

            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              closeAllDropdownsExcept(dropdown);
              dropdown.classList.toggle("active");
              trapFocus(next, el);
            }
          });
        });

        if (!this.quickAddDropdownClickHandler) {
          this.quickAddDropdownClickHandler = (event) => {
            if (
              !event.target.closest(".dropdown-opener") &&
              !event.target.closest(".share-buttons__list") &&
              !event.target.closest(".copy")
            ) {
              closeAllDropdownsExcept();
            } else if (event.target.closest(".copy")) {
              setTimeout(() => {
                closeAllDropdownsExcept();
              }, 800);
            }
          };

          document.addEventListener("click", this.quickAddDropdownClickHandler);
        }

        if (!this.quickAddDropdownKeydownHandler) {
          this.quickAddDropdownKeydownHandler = (event) => {
            if (event.key === "Escape") {
              closeAllDropdownsExcept();
            }
          };

          document.addEventListener(
            "keydown",
            this.quickAddDropdownKeydownHandler
          );
        }
      };

      initProductAccordion = () => {
        const toggles = document.querySelectorAll(".about__accordion-toggle");

        toggles.forEach((toggle) => {
          toggle.addEventListener("click", () => {
            const descriptions = toggle.parentElement.querySelectorAll(
              ".about__accordion-description"
            );

            if (!toggle.classList.contains("active")) {
              toggle.classList.add("active");

              const index = Array.from(toggle.parentElement.children).indexOf(
                toggle
              );
              const target = descriptions[index];

              if (target) {
                slideDown(toggle, target);
              }
            } else {
              toggle.classList.remove("active");

              descriptions.forEach((desc) => {
                slideUp(toggle, desc);
              });
            }
          });
        });
      };

      clearMediaClasses() {
        const mediaWrapper = this.modalContent.querySelector(".product__media");
        if (!mediaWrapper) return;

        const keepClasses = ["product__media", "product__media--no-media"];
        const filteredClasses = [...mediaWrapper.classList].filter((cssClass) =>
          keepClasses.includes(cssClass)
        );

        const productOuter = this.querySelector(".product__outer");
        if (productOuter)
          productOuter.classList.remove("product__outer--desktop-order");

        mediaWrapper.className = filteredClasses.join(" ");
      }

      injectMediaStylesheets(responseHTML) {
        const selectors = [
          'link[href*="component-deferred-media"]',
          'link[href*="component-product-model"]',
          'link[href*="component-model-viewer-ui"]',
          'link[id="ModelViewerStyle"]',
          'link[id="ModelViewerOverride"]',
        ];

        selectors.forEach((selector) => {
          const sourceLink = responseHTML.querySelector(selector);
          if (!sourceLink) return;

          const href = sourceLink.getAttribute("href");
          const id = sourceLink.id || null;

          const alreadyLoaded =
            (id && document.getElementById(id)) ||
            document.querySelector(`link[href="${href}"]`);

          if (alreadyLoaded) return;

          const link = document.createElement("link");
          link.rel = "stylesheet";
          link.href = href;
          if (id) link.id = id;
          document.head.appendChild(link);
        });
      }

      removeDOMElements() {
        const popup = this.productElement.querySelectorAll(".product-popup");
        if (popup)
          popup.forEach((el) => {
            el.remove();
          });

        const sku = this.productElement.querySelector(".product__sku");
        if (sku) sku.remove();

        const productDivider =
          this.productElement.querySelector(".product__divider");
        if (productDivider) productDivider.remove();

        const breadcrumb = this.productElement.querySelector(".breadcrumb");

        if (breadcrumb) breadcrumb.remove();

        const countdown =
          this.productElement.querySelector(".product-countdown");
        if (countdown) countdown.remove();

        const productStickyBar = this.productElement.querySelector(
          ".product-sticky-bar"
        );
        if (productStickyBar) productStickyBar.remove();

        const bigProductModal =
          this.productElement.querySelector("product-modal");
        if (bigProductModal) bigProductModal.remove();

        const productFullDetailsLink = this.productElement.querySelector(
          ".product__full-details-wrap"
        );
        if (productFullDetailsLink) productFullDetailsLink.remove();

        const pickupAvailability = this.productElement.querySelector(
          ".pickup-availability"
        );
        if (pickupAvailability) pickupAvailability.remove();

        const iconWithTexts = this.productElement.querySelectorAll(
          ".product__text-icons"
        );

        if (iconWithTexts)
          iconWithTexts.forEach((el) => {
            el.remove();
          });

        const recommendations = this.productElement.querySelector(
          "product-recommendations"
        );
        if (recommendations) recommendations.remove();

        const customLiquid =
          this.productElement.querySelector(".custom-liquid");
        if (customLiquid) customLiquid.remove();
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );
        this.productElement
          .querySelectorAll("variant-selects, variant-radios")
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      preventVariantURLSwitching() {
        if (this.modalContent.querySelector("variant-radios,variant-selects")) {
          this.modalContent
            .querySelector("variant-radios,variant-selects")
            .setAttribute("data-update-url", "false");
        }
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        const desktopColumns = product.classList.contains("product--columns");
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll(".product__media img");
        if (!mediaImages.length) return;

        let mediaImageSizes = "(min-width: 576px) 316px, calc(100vw - 4rem)";

        if (product.classList.contains("product--medium")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "605px");
        } else if (product.classList.contains("product--small")) {
          mediaImageSizes = mediaImageSizes.replace("715px", "495px");
        }

        mediaImages.forEach((img) =>
          img.setAttribute("sizes", mediaImageSizes)
        );
      }
    }
  );
}
